import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  FileBarChart,
  Users,
  GitBranch,
  Settings,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  Bell,
  ClipboardList,
  Trash2,
  Shield,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/audit", label: "Audit Log", icon: FileText },
  { to: "/reports", label: "Reports", icon: FileBarChart },
  { to: "/data-subjects", label: "Data Subjects", icon: Users },
  { to: "/lineage", label: "Lineage Explorer", icon: GitBranch },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/alerts", label: "Alerts", icon: Bell },
  { to: "/review-queue", label: "Review Queue", icon: ClipboardList },
  { to: "/erasure", label: "Erasure Requests", icon: Trash2 },
  { to: "/consent", label: "Consent", icon: Shield },
  { to: "/retention", label: "Retention Policies", icon: Clock },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

/**
 * Application navigation sidebar.
 *
 * Displays navigation links with icons. Supports collapsed state (icon-only)
 * and expanded state (icon + label). Active route is highlighted via NavLink.
 */
export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  return (
    <aside
      className={cn(
        "flex h-full flex-col border-r bg-card transition-all duration-300",
        collapsed ? "w-[60px]" : "w-[240px]",
      )}
    >
      {/* Logo */}
      <div
        className={cn(
          "flex h-16 items-center border-b px-4",
          collapsed ? "justify-center" : "gap-2",
        )}
      >
        <ShieldCheck className="h-6 w-6 shrink-0 text-primary" />
        {!collapsed && (
          <span className="font-semibold tracking-tight text-foreground">Compligraph</span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <TooltipProvider delayDuration={0}>
          <ul className="space-y-1 px-2">
            {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
              <li key={to}>
                {collapsed ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <NavLink
                        to={to}
                        className={({ isActive }) =>
                          cn(
                            "flex h-10 w-10 items-center justify-center rounded-md transition-colors",
                            isActive
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                          )
                        }
                        aria-label={label}
                      >
                        <Icon className="h-5 w-5" />
                      </NavLink>
                    </TooltipTrigger>
                    <TooltipContent side="right">{label}</TooltipContent>
                  </Tooltip>
                ) : (
                  <NavLink
                    to={to}
                    className={({ isActive }) =>
                      cn(
                        "flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                      )
                    }
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    <span>{label}</span>
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </TooltipProvider>
      </nav>

      {/* Collapse toggle */}
      <div className={cn("border-t p-2", collapsed ? "flex justify-center" : "")}>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="h-8 w-8"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>
    </aside>
  );
}
